import AddRounded from '@mui/icons-material/AddRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import HourglassBottomRounded from '@mui/icons-material/HourglassBottomRounded';
import RequestQuoteRounded from '@mui/icons-material/RequestQuoteRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, SearchField, type Column } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { EmptyState, PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { businessDate } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { todayIn } from '../inventory/model';
import { StatCard, StatGrid } from '../inventory/shared';
import {
  daysUntil,
  quotationKeys,
  STATUS_LABELS,
  STATUSES,
  validityText,
  type QuotationStatus,
  type QuotationSummary,
} from './model';
import { useExportSelection } from '../dataio/useExportSelection';
import { filesColumn, useAttachmentCounts } from '../attachments';

function useStatusCount(status: QuotationStatus) {
  return useQuery({
    queryKey: quotationKeys.statusCount(status),
    queryFn: () =>
      unwrap(api.GET('/quotations', { params: { query: { status, page: 1, pageSize: 1 } } })),
    select: (d) => d.page.total,
  });
}

export function QuotationsListPage() {
  const exportSelection = useExportSelection('quotation');
  const { can } = useAuth();
  const { format, settings } = useShop();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const today = todayIn(settings?.timeZone ?? 'Asia/Karachi');

  const status = (params.get('status') ?? '') as QuotationStatus | '';
  const q = params.get('q') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  const query = {
    status: status || undefined,
    q: q || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    pageSize,
  };
  const list = useQuery({
    queryKey: quotationKeys.list(query),
    queryFn: () => unwrap(api.GET('/quotations', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const drafts = useStatusCount('DRAFT');
  const sent = useStatusCount('SENT');
  const accepted = useStatusCount('ACCEPTED');
  const expired = useStatusCount('EXPIRED');

  const fileCounts = useAttachmentCounts(
    'QUOTATION',

    (list.data?.data ?? []).map((r) => r.id),
  );

  const columns: Column<QuotationSummary>[] = [
    {
      key: 'number',
      header: 'Quotation',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 650 }}>
            {r.number}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {businessDate(r.docDate, format)}
            {r.reference ? ` · ${r.reference}` : ''}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {r.contact.name}
          </Typography>
          {r.contact.phone && (
            <Typography variant="caption" color="text.secondary">
              {r.contact.phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'valid',
      header: 'Valid until',
      hideBelow: 'md',
      render: (r) => {
        const open = r.status === 'DRAFT' || r.status === 'SENT';
        const soon = open && r.validUntil !== null && daysUntil(r.validUntil, today) <= 3;
        return (
          <Box>
            <Typography variant="body2">
              {r.validUntil ? businessDate(r.validUntil, format) : '—'}
            </Typography>
            {(open || r.status === 'EXPIRED') && (
              <Typography
                variant="caption"
                sx={{
                  color:
                    r.status === 'EXPIRED'
                      ? 'error.main'
                      : soon
                        ? 'warning.main'
                        : 'text.secondary',
                }}
              >
                {validityText(r.validUntil, today)}
              </Typography>
            )}
          </Box>
        );
      },
    },
    { key: 'items', header: 'Items', align: 'right', hideBelow: 'lg', render: (r) => r.lineCount },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'sm',
      render: (r) => <StatusChip status={r.status} />,
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (r) => <Money value={r.total} strong />,
    },
  ];

  const filtered = Boolean(status || q || from || to);
  const newQuote = () => void navigate('/quotations/new');

  return (
    <>
      <PageHeader
        title="Quotations"
        subtitle="Price up work for customers, track their answers and turn accepted quotes into orders."
        actions={
          can('quotations.create') && (
            <Button variant="contained" startIcon={<AddRounded />} onClick={newQuote}>
              New quotation
            </Button>
          )
        }
      />
      <StatGrid>
        <StatCard
          label="Drafts"
          value={drafts.data ?? '—'}
          hint="Not sent yet"
          icon={<EditNoteRounded />}
          loading={drafts.isLoading}
        />
        <StatCard
          label="Awaiting reply"
          value={sent.data ?? '—'}
          hint="Sent to customers"
          icon={<SendRounded />}
          tone="info"
          loading={sent.isLoading}
        />
        <StatCard
          label="Accepted"
          value={accepted.data ?? '—'}
          hint="Ready to become orders"
          icon={<CheckCircleRounded />}
          tone="success"
          loading={accepted.isLoading}
        />
        <StatCard
          label="Expired"
          value={expired.data ?? '—'}
          hint="Extend or close them"
          icon={<HourglassBottomRounded />}
          tone="error"
          loading={expired.isLoading}
        />
      </StatGrid>

      <>
        <DataTable
          {...exportSelection.table}
          columns={[...columns, ...filesColumn<QuotationSummary>(fileCounts)]}
          rows={list.data?.data}
          rowKey={(r) => r.id}
          loading={list.isLoading}
          page={list.data?.page}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
          onRowClick={(r) => void navigate(`/quotations/${r.id}`)}
          toolbar={
            <>
              <SearchField
                value={q}
                onChange={(v) => set('q', v)}
                placeholder="Number or customer"
              />
              <TextField
                select
                label="Status"
                value={status}
                onChange={(e) => set('status', e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All statuses</MenuItem>
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="date"
                label="From"
                value={from}
                onChange={(e) => set('from', e.target.value)}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: to || undefined } }}
              />
              <TextField
                type="date"
                label="To"
                value={to}
                onChange={(e) => set('to', e.target.value)}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from || undefined } }}
              />
            </>
          }
          empty={
            filtered ? (
              <EmptyState
                icon={<RequestQuoteRounded />}
                title="No quotations match"
                body="Try another search, status or date range."
              />
            ) : (
              <EmptyState
                icon={<RequestQuoteRounded />}
                title="No quotations yet"
                body="Send customers a priced quote; once they accept, convert it into a sales order in one click."
                action={
                  can('quotations.create')
                    ? { label: 'New quotation', onClick: newQuote }
                    : undefined
                }
              />
            )
          }
        />
        {exportSelection.dialog}
      </>
    </>
  );
}
